import type { OnDestroy, OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ErrorHandler,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { FaceFilterType } from '@freelancer/components/face-filter';
import { FileRead } from '@freelancer/file-read';
import {
  MediaDeviceErrorCode,
  MediaDeviceFacingMode,
  MediaDevices,
} from '@freelancer/media-devices';
import { NativeSettingsService } from '@freelancer/native-settings';
import { Pwa } from '@freelancer/pwa';
import { TimeUtils } from '@freelancer/time-utils';
import { ModalRef } from '@freelancer/ui';
import { BannerAlertType } from '@freelancer/ui/banner-alert';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import {
  permissionReason,
  Permissions,
  PermissionType,
} from '@freelancer/ui/permissions';
import { PictureDisplay } from '@freelancer/ui/picture';
import { SpinnerType } from '@freelancer/ui/spinner';
import { FontWeight } from '@freelancer/ui/text';
import { VideoComponent } from '@freelancer/ui/video';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import type { Observable } from 'rxjs';
import { BehaviorSubject, firstValueFrom, from, Subscription } from 'rxjs';
import { last, map, take } from 'rxjs/operators';
import type { VideoRecordModalComponent } from '../video-record-modal.component';

@UntilDestroy({ className: 'VideoRecordComponent' })
@Component({
  standalone: false,
  selector: 'fl-video-record',
  template: `
    @if (showFaceFilter) {
      <ng-container [ngSwitch]="faceFilterType">
        <fl-face-filter-tessellation
          *ngSwitchCase="'tessellation'"
          [activeRecordingElementRef]="
            !finishedVideoLoaded
              ? activeRecordingElementRef
              : finishedVideoElementRef
          "
          [mirrorMode]="true"
          (faceFilterLoaded)="handleFaceFilterLoaded()"
        ></fl-face-filter-tessellation>
        <fl-face-filter-raccoon
          *ngSwitchCase="'raccoon'"
          [activeRecordingElementRef]="
            !finishedVideoLoaded
              ? activeRecordingElementRef
              : finishedVideoElementRef
          "
          [mirrorMode]="true"
          (faceFilterLoaded)="handleFaceFilterLoaded()"
        ></fl-face-filter-raccoon>
      </ng-container>
    }

    @if (!mediaDeviceErrorCode) {
      <div class="Container">
        <div class="VideoContainer">
          @if ((isLoading$ | flAsync) || (isLoadingFaceFilter$ | flAsync)) {
            <fl-spinner
              [overlay]="true"
              [type]="SpinnerType.BIRD"
            ></fl-spinner>
          }
          <fl-video
            #activeRecordingPlayer
            class="Video"
            [forceNative]="true"
            [disableControls]="true"
            [timeDisplay]="false"
            [class.Hide]="
              (isLoading$ | flAsync) || recordingState === 'finished'
            "
            [mirrorMode]="true"
          ></fl-video>
          <fl-video
            #finishedVideoPlayer
            class="Video FinishedVideoPlayer"
            [disableControls]="true"
            [forceNative]="true"
            [loop]="true"
            [class.Hide]="recordingState !== 'finished'"
            [mirrorMode]="true"
          ></fl-video>
          @if (showCameraTimer) {
            <div class="Countdown">
              <p class="text-title-90 font-light">
                {{ cameraTimerValue$ | flAsync }}
              </p>
            </div>
          }
        </div>
        @if (isLoading$ | flAsync) {
          <div class="LoadingContainer">
            <fl-banner-alert
              bannerTitle="Taking a moment..."
              i18n-bannerTitle="Banner alert title for loading the modal"
              [closeable]="false"
              [type]="BannerAlertType.INFO"
            >
              <p
                i18n="
                  Loading message while setting up the camera and microphone
                  permissions
                "
              >
                To set up your camera and microphone, please allow access to
                these devices when prompted.
              </p>
            </fl-banner-alert>
          </div>
        }
        @if (
          !showContentWhileRecording && subContent && !(isLoading$ | flAsync)
        ) {
          <div class="VideoSubContent">
            <ng-container [ngTemplateOutlet]="subContent"></ng-container>
          </div>
        }
        @if (
          showContentWhileRecording &&
          contentWhileRecording &&
          !(isLoading$ | flAsync)
        ) {
          <div class="VideoSubContent">
            <ng-container
              [ngTemplateOutlet]="contentWhileRecording"
            ></ng-container>
          </div>
        }
        @if (!(isLoading$ | flAsync)) {
          <div class="Controls">
            <div
              class="SideBtn SettingsBtn"
              (click)="settingsPage()"
            >
              <fl-icon
                [name]="'ui-cog-v2'"
                [useIconFont]="true"
                [fillIconFont]="true"
              ></fl-icon>
              <p
                class="text-body-16 font-bold"
                i18n="Button settings"
              >
                Settings
              </p>
            </div>
            <div class="BtnContainer">
              <!-- Record BTN -->
              @if (recordingState === 'initial') {
                <div
                  class="BtnWrapper Record"
                  (click)="startRecording()"
                >
                  <div class="BtnCustom">
                    <fl-icon
                      class="Icon"
                      [color]="IconColor.LIGHT"
                      [label]="'camera-icon'"
                      [name]="'ui-video'"
                      [useIconFont]="true"
                      [fillIconFont]="true"
                    ></fl-icon>
                  </div>
                </div>
              }
              <!-- Stop BTN -->
              @if (recordingState === 'recording') {
                <div
                  class="BtnWrapper"
                  (click)="stopRecording()"
                >
                  <div class="BtnCustom Stop">
                    <fl-icon [label]="'stop-icon'"></fl-icon>
                  </div>
                </div>
              }
              <!-- Save BTN -->
              @if (recordingState === 'finished') {
                <div
                  class="BtnWrapper Save"
                  (click)="closeModal()"
                >
                  <div class="BtnCustom">
                    <fl-icon
                      [name]="'ui-check'"
                      [label]="'tick-icon'"
                      [color]="IconColor.LIGHT"
                      [useIconFont]="true"
                      [fillIconFont]="true"
                      [size]="IconSize.LARGE"
                    ></fl-icon>
                  </div>
                </div>
              }
            </div>
            @if (recordingState !== 'finished') {
              <p
                class="text-body-16 RightBtn text-right font-bold"
                i18n="Time Left On Timer"
              >
                {{ timeLeft | number: '2.0-0' }}s
              </p>
            }
            @if (recordingState === 'finished') {
              <div
                class="SideBtn RightBtn"
                (click)="startRecording()"
              >
                <fl-icon
                  class="mb-xxxsmall"
                  [name]="'ui-refresh'"
                  [label]="'retake-icon'"
                  [useIconFont]="true"
                  [fillIconFont]="true"
                  [weight]="FontWeight.MEDIUM"
                ></fl-icon>
                <p
                  class="text-body-16 font-medium"
                  i18n="Button retake"
                >
                  Retake
                </p>
              </div>
            }
          </div>
        }
      </div>
    } @else {
      <div class="ErrorContainer">
        <fl-picture
          class="ErrorPicture mb-small"
          alt="Error occured"
          i18n-alt="Error occured image"
          [alignCenter]="true"
          [display]="PictureDisplay.BLOCK"
          [src]="'search/empty-or-error-state.svg'"
        ></fl-picture>
        <fl-banner-alert
          bannerTitle="There was a problem with your browser/device permissions"
          i18n-bannerTitle="Instructions for taking a video"
          [closeable]="false"
          [type]="BannerAlertType.ERROR"
        >
          <ng-container [ngSwitch]="mediaDeviceErrorCode">
            <ng-container
              *ngSwitchCase="MediaDeviceErrorCode.PERMISSION_DENIED"
            >
              <p i18n="Error message when PERMISSION_DENIED">
                The camera and/or microphone could not be used because access
                has been denied.
                @if (isNative) {
                  To grant permission, please go to your
                  <fl-link (click)="openApplicationSettings()">
                    app settings
                  </fl-link>
                  and ensure that Freelancer has access to both devices.
                }
                @if (!isNative) {
                  To grant permission, please go to your browser settings and
                  ensure that Freelancer has access to both devices.
                }
              </p>
              @if (isNative) {
                <fl-link
                  i18n="Link to open application settings"
                  (click)="openApplicationSettings()"
                >
                  Grant permission
                </fl-link>
              }
            </ng-container>
            <ng-container
              *ngSwitchCase="
                MediaDeviceErrorCode.MEDIA_DEVICE_API_NOT_SUPPORTED
              "
            >
              <p i18n="Error message when MEDIA_DEVICE_API_NOT_SUPPORTED">
                We can't seem to recognize your camera and/or microphone. Please
                check that your browser is updated to the latest version or use
                a different browser.
              </p>
            </ng-container>
            <ng-container
              *ngSwitchCase="MediaDeviceErrorCode.UNSUPPORTED_BROWSER"
            >
              <p i18n="Error message when UNSUPPORTED_BROWSER">
                The browser you are using is not supported. Please use a
                different browser or update to the latest version.
              </p>
            </ng-container>
            <!-- Show the default message for these errors: -->
            <!-- FAILED_TO_LOAD -->
            <!-- NOT_FOUND -->
            <!-- OVERCONSTRAINED -->
            <ng-container *ngSwitchDefault>
              <p
                i18n="
                  Default generic error message when trying to record a video
                "
              >
                Your camera and/or microphone could not be detected or accessed.
                Please ensure that both devices are connected and turned on.
                Additionally, check that the necessary permissions are granted.
                If the problem persists, you may visit our
                <fl-link [link]="'/support'">Help Center</fl-link>.
              </p>
            </ng-container>
          </ng-container>
        </fl-banner-alert>
      </div>
    }
  `,
  styleUrls: ['./video-record.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VideoRecordComponent implements OnInit, OnDestroy {
  private changeDetectorRef = inject(ChangeDetectorRef);
  private errorHandler = inject(ErrorHandler);
  private fileRead = inject(FileRead);
  private modalRef = inject<ModalRef<VideoRecordModalComponent>>(ModalRef);
  private mediaDevices = inject(MediaDevices);
  private nativeSettings = inject(NativeSettingsService);
  private permissions = inject(Permissions);
  private pwa = inject(Pwa);
  private timeUtils = inject(TimeUtils);

  IconColor = IconColor;
  IconSize = IconSize;
  FontWeight = FontWeight;
  BannerAlertType = BannerAlertType;
  MediaDeviceErrorCode = MediaDeviceErrorCode;
  PictureDisplay = PictureDisplay;
  SpinnerType = SpinnerType;

  @Input() faceFilterType: FaceFilterType = 'none';
  @Input() subContent?: TemplateRef<HTMLElement>;
  @Input() contentWhileRecording?: TemplateRef<HTMLElement>;

  @Output() canActivate = new EventEmitter<boolean>();
  @Output() onError = new EventEmitter<MediaDeviceErrorCode>();

  @ViewChild('activeRecordingPlayer') activeRecordingElementRef: VideoComponent;
  @ViewChild('finishedVideoPlayer') finishedVideoElementRef: VideoComponent;

  activeRecordingElm: HTMLVideoElement;
  finishedVideoElm: HTMLVideoElement;
  recordingState: 'initial' | 'recording' | 'finished' = 'initial';
  recordedFile: File;
  stream: MediaStream;
  timer: ReturnType<typeof setInterval>;
  timeLeft = 30;
  videoOnlyStream: MediaStream;
  showCameraTimer = false;
  recordTimeout = 3;
  cameraTimerValue$: Observable<number>;
  showContentWhileRecording = false;

  mediaDeviceErrorCode: MediaDeviceErrorCode;
  isNative: boolean;
  private isLoadingSubject$ = new BehaviorSubject<boolean>(false);
  isLoading$ = this.isLoadingSubject$.asObservable();

  private isLoadingFaceFilterSubject$ = new BehaviorSubject<boolean>(false);
  isLoadingFaceFilter$ = this.isLoadingFaceFilterSubject$.asObservable();

  showFaceFilter = false;

  subscriptions = new Subscription();

  finishedVideoLoaded = false;

  readonly VIDEO_MAX_LENGTH_S = 30;

  async closeModal(): Promise<void> {
    this.modalRef.close(this.recordedFile);
  }

  async ngOnInit(): Promise<void> {
    this.isNative = this.pwa.isNative();

    const cameraPermission = await this.permissions.requestPermissions({
      permission: PermissionType.CAMERA,
      reason: permissionReason.VIDEO,
    });

    if (!cameraPermission) {
      this.handleMediaDeviceError(MediaDeviceErrorCode.PERMISSION_DENIED);
      return;
    }

    const microphonePermission = await this.permissions.requestPermissions({
      permission: PermissionType.MICROPHONE,
      reason: permissionReason.MICROPHONE,
    });

    if (!microphonePermission) {
      this.handleMediaDeviceError(MediaDeviceErrorCode.PERMISSION_DENIED);
      return;
    }

    this.activeRecordingElm =
      this.activeRecordingElementRef.media.nativeElement;
    this.finishedVideoElm = this.finishedVideoElementRef.media.nativeElement;

    this.isLoadingSubject$.next(true);

    if (this.faceFilterType !== 'none') {
      this.isLoadingFaceFilterSubject$.next(true);
    }

    this.mediaDevices
      .getUserMediaVideoRecord({
        video: {
          width: 360,
          facingMode: MediaDeviceFacingMode.USER,
        },
        audio: true,
      })
      .then(async (streamResult): Promise<void> => {
        if (streamResult.status === 'error') {
          this.handleMediaDeviceError(
            streamResult.error ?? MediaDeviceErrorCode.FAILED_TO_LOAD,
          );
          return;
        }

        this.isLoadingSubject$.next(false);

        this.stream = streamResult.stream;
        this.videoOnlyStream = new MediaStream();
        this.stream.getVideoTracks().forEach(each => {
          this.videoOnlyStream.addTrack(each);
        });

        this.activeRecordingElm.srcObject = this.videoOnlyStream;

        // In UITest, Video.play() is resolved at GC, but at that time the element was already destroyed.
        // An Error `The play() request was interrupted by a call to pause()` was thrown.
        // Use an Observable here to ignore error after component get destroyed.
        this.subscriptions.add(
          from(this.activeRecordingElm.play()).subscribe(() => {
            this.onActiveVideoCanPlay();
            this.changeDetectorRef.markForCheck();
          }),
        );
      });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.activeRecordingElm?.pause();
    this.finishedVideoElm?.pause();
    this.stream?.getTracks().forEach(track => track.stop());
    this.videoOnlyStream?.getTracks().forEach(track => track.stop());
    this.stopVideoTimer();
  }

  private handleMediaDeviceError(error: MediaDeviceErrorCode): void {
    this.isLoadingSubject$.next(false);
    this.mediaDeviceErrorCode = error;
    this.changeDetectorRef.markForCheck();
  }

  settingsPage(): void {
    // TODO: T267853 - in next iteration
    console.log('settings');
  }

  setShowCameraTimer(value: boolean): void {
    this.showCameraTimer = value;
    this.changeDetectorRef.markForCheck();
  }

  startVideoTimer(): void {
    this.timer = setInterval(() => {
      if (this.timeLeft <= 0) {
        this.stopRecording();
        this.stopVideoTimer();
      } else {
        this.timeLeft -= 1;
        this.changeDetectorRef.markForCheck();
      }
    }, 1000);
  }

  stopVideoTimer(): void {
    if (this.timer) {
      clearTimeout(this.timer);
    }
    this.timeLeft = this.VIDEO_MAX_LENGTH_S;
    this.changeDetectorRef.markForCheck();
  }

  async startRecording(): Promise<void> {
    if (!this.showCameraTimer) {
      this.setShowCameraTimer(true);
      this.cameraTimerValue$ = this.timeUtils.rxTimer(0, 1000).pipe(
        take(this.recordTimeout + 1),
        map(val => this.recordTimeout - val),
      );
      await firstValueFrom(
        this.cameraTimerValue$.pipe(last(), untilDestroyed(this)),
      ).then(() => {
        this.setShowCameraTimer(false);
        this.finishedVideoLoaded = false;
        this.finishedVideoElm?.pause();
        this.changeDetectorRef.markForCheck();
        if (this.contentWhileRecording) {
          this.handleContentWhileRecording();
        }
        this.startVideoTimer();

        const options = {
          mimeType: this.mediaDevices.isMimeTypeSupported('video/webm')
            ? 'video/webm'
            : 'video/mp4',
        };

        try {
          this.mediaDevices.initMediaRecorder({
            mediaStream: this.stream,
            options,
          });
          this.mediaDevices.mediaRecorderFileCallback({
            callbackFn: (event: Event, file: File) => {
              try {
                this.recordedFile = file;
                this.fileRead
                  .getBase64StringFromFile(this.recordedFile)
                  .then((base64String: string) => {
                    // TODO: T283475 - getBase64StringFromFile returns video/webm for mp4 files.
                    // This is a workaround to replace it with video/mp4.
                    const base64StringModified =
                      this.mediaDevices.isMimeTypeSupported('video/webm')
                        ? base64String
                        : base64String.replace('video/webm', 'video/mp4');
                    this.finishedVideoElm.src = base64StringModified;
                    this.changeDetectorRef.markForCheck();

                    this.subscriptions.add(
                      from(this.finishedVideoElm.play()).subscribe(() => {
                        this.finishedVideoLoaded = true;
                        this.changeDetectorRef.markForCheck();
                      }),
                    );
                  });
              } catch (e: any) {
                this.errorHandler.handleError(e);
              }
            },
            fileName: this.mediaDevices.isMimeTypeSupported('video/webm')
              ? 'videoUpload.webm'
              : 'videoUpload.mp4',
          });
          this.mediaDevices.startMediaRecorder();
          this.recordingState = 'recording';
          this.changeDetectorRef.markForCheck();
        } catch (e: any) {
          this.errorHandler.handleError(e);
        }
      });
    }
  }

  stopRecording(): void {
    this.recordingState = 'finished';
    this.mediaDevices.stopMediaRecorder();
    this.stopVideoTimer();
    this.changeDetectorRef.markForCheck();
  }

  private handleContentWhileRecording(): void {
    if (this.recordingState === 'finished') {
      return;
    }

    this.showContentWhileRecording = true;
    this.changeDetectorRef.markForCheck();
  }

  openApplicationSettings(): void {
    this.nativeSettings.openAppSettings();
  }

  onActiveVideoCanPlay(): void {
    if (this.faceFilterType !== 'none') {
      // Ref T285345. Only initialize the face filter once the video is playable.
      // This fixes the issue that the face detection yields very low values.
      this.showFaceFilter = true;
      this.changeDetectorRef.markForCheck();
    }
  }

  handleFaceFilterLoaded(): void {
    this.isLoadingSubject$.next(false);
    this.isLoadingFaceFilterSubject$.next(false);
  }
}
